import { useCallback, useEffect, useRef, useState } from "react";
import { fetchVideoMetadata } from "../api";

// Loads the metadata service dataset while `enabled`, for the month in
// `query` ({ month: "YYYY-MM", by: "video" | "processed" }; no month =
// everything). `reload(true)` asks the service to re-extract every file.
export default function useVideoMetadata(enabled, query = {}) {
  const month = query?.month ?? null;
  const by = query?.by ?? null;
  const [data, setData] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);
  const [token, setToken] = useState(0);
  const refreshRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const refresh = refreshRef.current;
    refreshRef.current = false;
    setStatus("loading");
    setError(null);

    fetchVideoMetadata({ refresh, month, by })
      .then((dataset) => {
        if (cancelled) return;
        setData(dataset);
        setStatus("ready");
      })
      .catch((err) => {
        if (cancelled) return;
        setStatus("error");
        setError(err?.message || "Request failed");
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, token, month, by]);

  // Poll while the backend is still reading files.
  useEffect(() => {
    if (!enabled || status !== "ready" || !data?.scan?.running) return undefined;
    const timer = setTimeout(() => setToken((t) => t + 1), 4000);
    return () => clearTimeout(timer);
  }, [enabled, status, data]);

  const reload = useCallback((refresh = false) => {
    refreshRef.current = refresh;
    setToken((t) => t + 1);
  }, []);

  return { data, status, error, reload };
}
